import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { Button, Table, Tag } from '@bedrock/components';
import { Plus } from '@bedrock/icons-react';
import ProTableForm, {
  FormTableRef,
} from '@mpc/common/src/components/ProTableForm';
import {
  enums,
  type IpCooperationDemandDetailVoLegalRiskRecord,
  type IpCooperationRiskLevelEnum,
} from '#services/api';

const RISK_LEVEL_MAP: Record<
  IpCooperationRiskLevelEnum,
  { text: string; color: string }
> = {
  HIGH: { text: '高', color: 'light-red' },
  MEDIUM: { text: '中', color: 'light-orange' },
  LOW: { text: '低', color: 'light-grey' },
};

// SForm select 选项：引用 api.ts 服务端枚举（enums.IpCooperation*Enum.values()），避免前后端枚举漂移
const RISK_TYPE_OPTIONS = enums.IpCooperationRiskTypeEnum.values();
const RISK_LEVEL_OPTIONS = enums.IpCooperationRiskLevelEnum.values();
const RISK_STATUS_OPTIONS = enums.IpCooperationRiskStatusEnum.values();

interface LegalComplianceReportProps {
  value?: IpCooperationDemandDetailVoLegalRiskRecord[];
  onChange?: (value: IpCooperationDemandDetailVoLegalRiskRecord[]) => void;
  /** 详情只读：隐藏新增/删除，行内单元格纯展示 */
  view?: boolean;
}

/** 父表单 watchChildFormValidate 在提交时调用的校验能力 */
export interface LegalComplianceReportRef {
  validateFields: () => Promise<unknown>;
}

/** 空风险记录模板（字段对齐后端 IpCooperationDemandLegalReportFormRiskRecord，建议措施为 suggest） */
const emptyRiskRecord = (): IpCooperationDemandDetailVoLegalRiskRecord => ({
  risk_type: undefined,
  risk_level: undefined,
  risk_desc: '',
  suggest: '',
  source: '',
  status: undefined,
});

/**
 * 法务风险记录表格（受控组件，参考 OnlineDetailTable 使用 ProTableForm）
 * value/onChange 由父表单 Form.Item 注入，编辑态数据实时回写父表单 formData.legal_risk_records，
 * 供 Footer.handleLegalReport 提交。
 */
const LegalComplianceReport = forwardRef<
  LegalComplianceReportRef,
  LegalComplianceReportProps
>(({ value, onChange, view = false }, ref) => {
  const tableRef = useRef<FormTableRef>(null);

  // 暴露校验能力：委托内部 ProTableForm.form.validateFields()（镜像 OnlineDetailTable 范式）
  useImperativeHandle(
    ref,
    () => ({
      validateFields: () => {
        if (!tableRef.current?.form?.validateFields) {
          return Promise.resolve();
        }
        return tableRef.current.form.validateFields().catch((error: any) => {
          const errMessage = error?.errorFields?.[0]?.errors?.[0];
          return Promise.reject(errMessage);
        });
      },
    }),
    []
  );

  const formColumns = useMemo(() => {
    return ({
      remove,
    }: {
      add: any;
      remove: (i: number | number[]) => void;
    }) => [
      {
        title: '风险类型',
        dataIndex: 'risk_type',
        width: 140,
        // 编辑态必填：列顶层 required 驱动表头星号 + SFormItem 校验；view 只读不显示星号
        required: !view,
        ...(view
          ? {
              render: (
                _t: any,
                record: IpCooperationDemandDetailVoLegalRiskRecord
              ) =>
                RISK_TYPE_OPTIONS?.find(
                  (item) => item.value === record.risk_type
                )?.name || '--',
            }
          : {
              formItem: {
                componentClass: 'select',
                elementProps: {
                  options: RISK_TYPE_OPTIONS,
                  placeholder: '请选择',
                  className: 'w-[120px]',
                },
              },
            }),
      },
      {
        title: '风险等级',
        dataIndex: 'risk_level',
        width: 90,
        // 编辑态必填（对齐风险类型列）
        required: !view,
        ...(view
          ? {
              render: (
                _t: any,
                record: IpCooperationDemandDetailVoLegalRiskRecord
              ) => {
                const conf = record.risk_level
                  ? RISK_LEVEL_MAP[record.risk_level]
                  : undefined;
                return conf ? (
                  <Tag size="small" text={conf.text} color={conf.color} />
                ) : (
                  '--'
                );
              },
            }
          : {
              formItem: {
                componentClass: 'select',
                elementProps: {
                  options: RISK_LEVEL_OPTIONS,
                  placeholder: '请选择',
                  className: 'w-[96px]',
                },
              },
            }),
      },
      {
        title: '风险描述',
        dataIndex: 'risk_desc',
        width: 284,
        ...(view
          ? {
              render: (
                _t: any,
                record: IpCooperationDemandDetailVoLegalRiskRecord
              ) => record.risk_desc || '--',
            }
          : {
              // textarea + maxLength 800：对齐 MarketingClosedLoop TableList 范式（showCount + maxLength + rows）
              formItem: {
                componentClass: 'textarea',
                elementProps: {
                  rows: 2,
                  maxLength: 800,
                  showCount: true,
                  placeholder: '请输入，最多800字符',
                },
              },
            }),
      },
      {
        title: '建议措施',
        dataIndex: 'suggest',
        width: 284,
        ...(view
          ? {
              render: (
                _t: any,
                record: IpCooperationDemandDetailVoLegalRiskRecord
              ) => record.suggest || '--',
            }
          : {
              formItem: { componentClass: 'input', elementProps: { rows: 2 } },
            }),
      },
      {
        title: '信息来源',
        dataIndex: 'source',
        width: 284,
        ...(view
          ? {
              render: (
                _t: any,
                record: IpCooperationDemandDetailVoLegalRiskRecord
              ) => record.source || '--',
            }
          : { formItem: { componentClass: 'input' } }),
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 160,
        // 编辑态必填（对齐风险类型列）
        required: !view,
        ...(view
          ? {
              render: (
                _t: any,
                record: IpCooperationDemandDetailVoLegalRiskRecord
              ) => (
                <Tag
                  size="small"
                  text={
                    RISK_STATUS_OPTIONS?.find(
                      (item) => item.value === record.status
                    )?.name || record.status
                  }
                  color="light-orange"
                />
              ),
            }
          : {
              formItem: {
                componentClass: 'select',
                elementProps: {
                  options: RISK_STATUS_OPTIONS,
                  placeholder: '请选择',
                },
              },
            }),
      },
      // 详情只读：不显示「操作」列
      ...(view
        ? []
        : [
            {
              title: '操作',
              dataIndex: 'operate',
              width: 60,
              fixed: 'right' as const,
              render: (
                _t: any,
                _r: IpCooperationDemandDetailVoLegalRiskRecord,
                index: number
              ) => (
                <Button
                  type={'link'}
                  size="small"
                  danger
                  onClick={() => remove(index)}
                >
                  删除
                </Button>
              ),
            },
          ]),
    ];
  }, [view]);

  const handleAdd = () => {
    tableRef.current?.add(emptyRiskRecord());
  };

  return (
    <ProTableForm<IpCooperationDemandDetailVoLegalRiskRecord>
      ref={tableRef}
      value={value}
      onChange={onChange}
      sortable={false}
      size="small"
      scroll={{ x: 'max-content' }}
      formColumns={formColumns as any}
      summary={
        view
          ? undefined
          : () => (
              <Table.Summary>
                <Table.Summary.Row style={{ background: '#fff' }}>
                  <Table.Summary.Cell index={0} colSpan={7}>
                    <Button
                      size="small"
                      type="checked-neutral"
                      icon={<Plus />}
                      onClick={handleAdd}
                    >
                      添加风险次数
                    </Button>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )
      }
    />
  );
});

LegalComplianceReport.displayName = 'LegalComplianceReport';

export default LegalComplianceReport;
